<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMobileDevice } from 'dashboard/composables/useMobileDevice';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import { closeActiveMediaViewer } from 'dashboard/composables/aceleresMediaViewerCoordinator';
import {
  createConversationLoadGuard,
  isConversationHydrated as hasHydratedConversation,
  shouldPreviewConversation,
} from './conversationLoadGuard';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.conversationLoadGuard.invalidate();
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const isMobileViewport = useMobileDevice();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      isMobileViewport,
    };
  },
  data() {
    return {
      showSearchModal: false,
      escapeKeyHandler: null,
      conversationLoadGuard: createConversationLoadGuard(),
      conversationLoadState: 'idle',
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return !this.isMobileConversationActive;
    },
    isFunnelEmbed() {
      return (
        typeof window !== 'undefined' && window.name === 'aceleres-funil-chat'
      );
    },
    isConversationHydrated() {
      return hasHydratedConversation({
        conversationId: this.conversationId,
        currentChat: this.currentChat,
        loadState: this.conversationLoadState,
      });
    },
    isConversationError() {
      return (
        Number(this.conversationId || 0) > 0 &&
        this.conversationLoadState === 'error'
      );
    },
    showMessageView() {
      if (!this.isFunnelEmbed) {
        return (
          !!this.conversationId ||
          !!this.currentChat.id ||
          !this.isOnExpandedLayout
        );
      }

      return (
        this.isConversationHydrated ||
        this.isConversationError ||
        (!this.conversationId && !this.isOnExpandedLayout)
      );
    },
    isOnExpandedLayout() {
      return this.isMobileViewport;
    },

    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },
    isMobileConversationActive() {
      return this.isMobileViewport && Number(this.conversationId) > 0;
    },
    isMobileConversationEmpty() {
      return this.isMobileViewport && !this.isMobileConversationActive;
    },
  },
  watch: {
    conversationId(nextConversationId, previousConversationId) {
      if (
        Number(nextConversationId) !== Number(previousConversationId) &&
        this.uiSettings.is_contact_sidebar_open
      ) {
        this.updateUISettings({ is_contact_sidebar_open: false });
      }
      this.conversationLoadState = nextConversationId ? 'loading' : 'idle';
      if (!this.conversationId) {
        this.conversationLoadGuard.invalidate();
        this.$store.dispatch('clearSelectedState');
      }
      this.handleConversationListUpdate();
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.conversationLoadState = 'idle';
      this.$store.dispatch('clearSelectedState');
    } else {
      this.conversationLoadState = 'loading';
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.handleConversationListUpdate();
    });
    this.bindEscapeHandler();
  },

  beforeUnmount() {
    this.unbindEscapeHandler();
  },

  methods: {
    isVisibleElement(el) {
      if (!el || !el.isConnected) return false;
      const style = window.getComputedStyle(el);
      if (
        !style ||
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.opacity === '0'
      ) {
        return false;
      }
      if (
        el.hasAttribute('hidden') ||
        el.getAttribute('aria-hidden') === 'true'
      ) {
        return false;
      }
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    },

    findBlockingModal() {
      const selectors = [
        '[role="dialog"]',
        '[aria-modal="true"]',
        'dialog[open]',
        '.modal',
        '.modal-dialog',
        '.drawer',
        '.overlay',
        '.backdrop',
      ];

      for (let i = 0; i < selectors.length; i += 1) {
        const nodes = document.querySelectorAll(selectors[i]);
        for (let j = 0; j < nodes.length; j += 1) {
          if (this.isVisibleElement(nodes[j])) return nodes[j];
        }
      }

      return null;
    },

    onGlobalEscapeKey(event) {
      if (!event || (event.key !== 'Escape' && event.key !== 'Esc')) return;

      if (closeActiveMediaViewer()) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        return;
      }

      if (this.showSearchModal) {
        this.closeSearch();
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        return;
      }

      if (this.findBlockingModal()) {
        return;
      }

      if (!this.conversationId) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      if (this.isFunnelEmbed && window.parent !== window) {
        // O Funil controla o painel que contém este iframe. Só ele pode
        // removê-lo da tela; limpar a seleção ou navegar ao dashboard deixa
        // o iframe branco aberto sobre o board.
        window.parent.postMessage(
          { aceleresFunilChat: 'close-conversation' },
          '*'
        );
        return;
      }

      this.$store.dispatch('clearSelectedState');
      this.$router.push(this.accountScopedUrl('dashboard'));
    },

    bindEscapeHandler() {
      if (this.escapeKeyHandler) return;

      this.escapeKeyHandler = event => {
        this.onGlobalEscapeKey(event);
      };

      window.addEventListener('keydown', this.escapeKeyHandler, true);
      document.addEventListener('keydown', this.escapeKeyHandler, true);
    },

    unbindEscapeHandler() {
      if (!this.escapeKeyHandler) return;

      window.removeEventListener('keydown', this.escapeKeyHandler, true);
      document.removeEventListener('keydown', this.escapeKeyHandler, true);
      this.escapeKeyHandler = null;
    },

    onConversationLoad() {
      this.handleConversationListUpdate();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.handleConversationListUpdate();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        this.conversationLoadState = 'idle';
        this.$store.dispatch('clearSelectedState');
        return;
      }

      this.conversationLoadState = 'loading';
      // A tab switch can replace the list with another page and remove an
      // already-opened conversation from the main array. Restore the memory
      // copy before deciding whether messages must be fetched again.
      this.$store.dispatch(
        'restoreConversationFromSessionCache',
        this.conversationId
      );
      this.setActiveChat();
    },
    handleConversationListUpdate() {
      this.fetchConversationIfUnavailable();
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    notifyActiveConversationLoadStart(conversationId) {
      if (typeof window === 'undefined' || !conversationId) {
        return;
      }

      window.startZoraxyActiveConversationLoad?.({
        targetCount: 1,
        conversationId,
      });
    },
    notifyActiveConversationLoadDone() {
      if (typeof window === 'undefined') {
        return;
      }

      window.finishZoraxyActiveConversationLoad?.();
    },
    markFunilConversationLoading() {
      if (typeof window === 'undefined' || window.parent === window) return;
      window.aceleresFunilConversationReady = 0;
    },
    markFunilConversationReady(conversationId) {
      if (
        typeof window === 'undefined' ||
        window.parent === window ||
        !conversationId
      ) {
        return;
      }

      window.aceleresFunilConversationReady = Number(conversationId);
    },
    async confirmConversationReady(loadRequest, conversationId) {
      if (!this.conversationLoadGuard.isCurrent(loadRequest, conversationId)) {
        return false;
      }

      this.conversationLoadState = 'ready';
      await new Promise(resolve => {
        this.$nextTick(() => resolve());
      });

      if (!this.conversationLoadGuard.isCurrent(loadRequest, conversationId)) {
        return false;
      }

      this.markFunilConversationReady(conversationId);
      return true;
    },
    async setActiveChat() {
      const conversationId = parseInt(this.conversationId, 10);
      const loadRequest = this.conversationLoadGuard.begin(conversationId);
      this.markFunilConversationLoading();
      this.conversationLoadState = conversationId ? 'loading' : 'idle';

      if (!conversationId) {
        this.$store.dispatch('clearSelectedState');
        return;
      }

      const selectedConversation = this.findConversation();
      if (
        Number(selectedConversation?.id || 0) ===
          Number(this.currentChat?.id || 0) &&
        selectedConversation?.dataFetched === true
      ) {
        const ready = await this.confirmConversationReady(
          loadRequest,
          conversationId
        );
        if (!ready) return;
        this.notifyActiveConversationLoadDone();
        return;
      }

      // Never keep the previous conversation rendered while the new card is
      // being hydrated. The preview is useful for a sidebar click, but in the
      // funnel iframe it makes the old conversation look like the new one
      // until the request finishes.
      if (Number(this.currentChat?.id || 0) !== conversationId) {
        await this.$store.dispatch('clearSelectedState');
      }

      const { messageId } = this.$route.query;
      const hasPrefetchedConversation =
        selectedConversation?.dataFetched === true;
      const canOpenOptimistically =
        selectedConversation &&
        shouldPreviewConversation({
          embedded: this.isFunnelEmbed,
          currentConversationId: this.currentChat.id,
          targetConversationId: conversationId,
        });

      this.notifyActiveConversationLoadStart(conversationId);

      if (canOpenOptimistically) {
        await this.$store.dispatch('previewActiveChat', {
          data: selectedConversation,
        });
        if (
          !this.conversationLoadGuard.isCurrent(loadRequest, conversationId)
        ) {
          return;
        }
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
      }

      if (hasPrefetchedConversation && selectedConversation) {
        try {
          await this.$store.dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          });
          if (
            !this.conversationLoadGuard.isCurrent(loadRequest, conversationId)
          ) {
            return;
          }
          const ready = await this.confirmConversationReady(
            loadRequest,
            conversationId
          );
          if (!ready) return;
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
        } finally {
          if (
            this.conversationLoadGuard.isCurrent(loadRequest, conversationId)
          ) {
            this.notifyActiveConversationLoadDone();
          }
        }
        return;
      }

      try {
        await this.$store.dispatch('getConversation', conversationId);
      } catch {
        if (this.conversationLoadGuard.isCurrent(loadRequest, conversationId)) {
          this.conversationLoadState = 'error';
          this.notifyActiveConversationLoadDone();
        }
        return;
      }

      if (!this.conversationLoadGuard.isCurrent(loadRequest, conversationId)) {
        return;
      }

      try {
        const hydratedConversation = this.findConversation();
        if (!hydratedConversation) {
          this.conversationLoadState = 'error';
          return;
        }

        if (
          Number(hydratedConversation.id || 0) !==
            Number(this.currentChat?.id || 0) ||
          hydratedConversation.dataFetched !== true
        ) {
          await this.$store.dispatch('setActiveChat', {
            data: hydratedConversation,
            after: messageId,
          });
          if (
            !this.conversationLoadGuard.isCurrent(loadRequest, conversationId)
          ) {
            return;
          }
          const ready = await this.confirmConversationReady(
            loadRequest,
            conversationId
          );
          if (!ready) return;
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
        } else {
          const ready = await this.confirmConversationReady(
            loadRequest,
            conversationId
          );
          if (!ready) return;
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
        }
      } finally {
        if (this.conversationLoadGuard.isCurrent(loadRequest, conversationId)) {
          this.notifyActiveConversationLoadDone();
        }
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
  },
};
</script>

<template>
  <section
    class="painel-atendimento flex w-full h-full min-w-0"
    :class="{
      'acl-mobile-conversation-active': isMobileConversationActive,
      'acl-mobile-conversation-empty': isMobileConversationEmpty,
      'acl-conversation-mobile-page': isMobileConversationActive,
      'acl-desktop-conversation-layout': !isMobileViewport,
    }"
  >
    <ChatList
      v-if="isMobileViewport"
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      @conversation-load="onConversationLoad"
    />
    <ConversationBox
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id" />
    </ConversationBox>
    <ConversationSidebar v-if="shouldShowSidebar" :current-chat="currentChat" />
    <CmdBarConversationSnooze />
  </section>
</template>

<style lang="scss">
.painel-atendimento {
  .text-xs {
    font-size: 0.8125rem;
  }
  .text-sm {
    font-size: 0.9375rem;
  }
  .text-base {
    font-size: 1.0625rem;
  }
}
</style>
